import React, { memo } from "react";
import { FaCheck, FaExclamation, FaFileLines, FaMinus, FaTriangleExclamation, FaXmark } from "react-icons/fa6";
import moment from "moment";
import { fromAdToBs, rupeeConverter } from "../../global/function";
import type { Bill } from "../../queries/bill.queries";

function formatDualDate(date: string) {
	return (
		<>
			{fromAdToBs(date, "DD MMMM, YYYY")}{" "}
			<em>({moment(date).format("DD MMMM, YYYY")})</em>
		</>
	);
}

// Shared by bill-list's row/table badge and bill-summary-card.component.tsx's
// detail-view badge cluster, so the "which app, synced or not" copy only
// exists in one place. bill_meta is only present when this bill is linked to
// an external system (Model A: pushed in; Model B: customer has an
// external_customer_id — see bill.controller.js's createBillMetaIfNeeded/
// resetBillMetaIfNeeded).
export function getSyncBadgeInfo(billMeta: NonNullable<Bill["bill_meta"]>) {
	const synced = billMeta.ext_synced === "synced";
	const appName = billMeta.system_type.toUpperCase();
	return {
		synced,
		label: synced ? `Synced to ${appName}` : `${appName} not synced`,
	};
}

function renderSyncTag(bill: Bill) {
	if (!bill.bill_meta) return null;
	const { synced, label } = getSyncBadgeInfo(bill.bill_meta);
	return (
		<div className={`bill-status ${synced ? "synced" : "sync-pending"}`}>
			{label}
		</div>
	);
}

// Exported so bill-table-columns.tsx's Status column can reuse the exact
// same badge markup/logic instead of a second hand-rolled copy.
export function renderBillTags(bill: Bill) {
	if (bill.bill_canceled) {
		return (
			<div className="bill-tags">
				<div className="bill-status canceled">Canceled</div>
				{renderSyncTag(bill)}
			</div>
		);
	}
	return (
		<div className="bill-tags">
			<div className={`bill-status ${bill.bill_void ? "credited" : "bill"}`}>
				{bill.bill_void ? "Credit Note" : "Bill"}
			</div>
			{bill.ird_bill === "pending" && <div className="bill-status sync-pending">IRD Not Synced</div>}
			{bill.ird_bill === "failed" && <div className="bill-status credited">IRD Failed</div>}
			{renderSyncTag(bill)}
		</div>
	);
}

function renderBillIcon(bill: Bill) {
	if (bill.bill_canceled) {
		return (
			<div className="bill-icon">
				<FaFileLines />
				<div className="icon-indicator void">
					<FaMinus />
				</div>
			</div>
		);
	}
	if (bill.ird_bill === "success") {
		return (
			<div className="bill-icon">
				<FaFileLines />
				<div className={`icon-indicator ${bill.bill_void ? "void" : ""}`}>
					{bill.bill_void ? (
						<FaExclamation />
					) : (
						<FaCheck />
					)}
				</div>
			</div>
		);
	}
	return (
		<div className="bill-icon">
			<FaTriangleExclamation />
			<div className="icon-indicator void">
				<FaXmark />
			</div>
		</div>
	);
}

interface BillListItemProps {
	bill: Bill;
	selected: boolean;
	onSelect: (bill: Bill) => void;
}

// Extracted + memoized so selecting a bill (or any other unrelated state
// change in the parent list) doesn't re-run every row's Nepali-date
// conversion — previously inline in a .map() with no memoization, which
// mattered little at ~20 rows but matters a lot once infinite scroll can
// load hundreds. Keyed on bill_id by the caller instead of array index (a
// stable identity across page loads/re-fetches, unlike position).
function BillListItem({ bill, selected, onSelect }: BillListItemProps) {
	return (
		<div
			className={`list-item-container ${selected ? "selected" : ""}`}
			onClick={() => onSelect(bill)}
		>
			{renderBillIcon(bill)}
			<div className="bill-item-details">
				<div className="details-row">
					<div className="bill-mainText cap-display">{bill.buyer_name}</div>
					<div className="bill-subText font-xs"># {bill.invoice_number}</div>
				</div>
				<div className="bill-subText">
					Invoice: {formatDualDate(bill.invoice_date)}
				</div>
				<div className="bill-subText">
					Sale: {formatDualDate(bill.sales_date)}
				</div>
				<div className="details-row flex-end">
					{renderBillTags(bill)}
					<div className="bill-mainText font-xl">Rs. {rupeeConverter(bill.total_sales)} </div>
				</div>
				{bill.ird_bill === "success" && bill.ird_synced_at && (
					<div className="bill-ird-synced-at">
						IRD synced · {moment(bill.ird_synced_at).fromNow()}
					</div>
				)}
			</div>
		</div>
	);
}

export default memo(BillListItem);
